import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSwitch, useSelect } from '@chilecompra/react-kit'
import { Card, DataTable, Select, Switch, Typography } from '@chilecompra/react-kit/components'
import {
  DISTRIBUTION_DICTIONARY,
  DISTRIBUTION_EMT_OPTIONS,
  INDICATORS_EMT_DICTIONARY,
  INDICATORS_EMT_TRANSLATION_DICTIONARY,
  OPTION_ALL,
  INDICATORS_EMT_OPTIONS
} from '../../config/settings/constants'
import StackedBarChart from '../../components/Charts/StackedBarChart'
import { onGetSmallerCompaniesBySectorThunk } from './SmallerCompaniesBySector.actions'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import { getListMonth, getMonth } from '../../modules/utils/months'
import { generateYears } from '../../modules/utils/years'
import InfoEMTDialog from '../../components/Dialogs/InfoEMTDialog'
import {
  DialogLink,
  CardCustom,
  Content,
  Description,
  ExclamationIcon,
  Info,
  InfoCircle,
  GridContainer,
  GridItem,
  Root,
  Title
} from './SmallerCompaniesBySector.styles'
import InfoSmallerCompaniesBySector from '../../components/Infographics/InfoSmallerCompaniesBySector'

/**
 * The SmallerCompaniesBySector's container.
 */
const SmallerCompaniesBySector = () => {
  const dispatch = useDispatch()
  const { endYear, latestUpdate, sectors: sectorOptions, loading } = useSelector(state => state.topNavbar)
  const { csv, data, dataAccumulated, series } = useSelector(state => state.smallerCompaniesBySector)
  const months = useMemo(() => getListMonth(), [])
  const [helpOpen, setHelpOpen] = useState(false)
  const handleCloseHelp = useCallback(() => setHelpOpen(false), [])
  const handleOpenHelp = useCallback(() => setHelpOpen(true), [])

  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: getMonth() === 0 ? endYear - 1 : endYear,
    changeCallback: updatedValue => {
      dispatch(onGetSmallerCompaniesBySectorThunk({ year: updatedValue, sector: sectorValue }))
    },
    overwriteCallback: () => endYear
  })
  const { value: distributionValue, onChange: handleChangeDistribution } = useSelect({
    initialValue: DISTRIBUTION_DICTIONARY.Monthly
  })

  const { value: indicatorValue, onChange: handleChangeIndicador } = useSelect({
    initialValue: INDICATORS_EMT_DICTIONARY.amount
  })

  const { value: sectorValue, onChange: handleChangeSector } = useSelect({
    initialValue: OPTION_ALL
  })

  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch()

  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () => exportToImage('smaller-companies-by-sector-chart', `cifras-mensuales-emt_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `cifras-mensuales-emt_${yearValue}`)
  })
  const yearsOptions = useMemo(() => generateYears(endYear), [endYear])

  const enhancedData = useMemo(() => {
    return series.map(datum => {
      let valueArray = []
      const isAccumulated = distributionValue === DISTRIBUTION_DICTIONARY.Accumulated
      const indicator = INDICATORS_EMT_TRANSLATION_DICTIONARY[indicatorValue]
      switch (indicator) {
        case 'amount':
          valueArray = isAccumulated
            ? currencyValue
              ? datum.amountAccumulatedUSD
              : datum.amountAccumulated
            : currencyValue
              ? datum.amountUSD
              : datum.amount
          break
        case 'orders':
          valueArray = isAccumulated ? datum.ordersAccumulated : datum.orders
          break
        case 'quantity':
          valueArray = isAccumulated ? datum.quantityAccumulated : datum.quantity
          break
        default:
          break
      }
      return {
        name: datum.name,
        data: valueArray ?? [],
        color: datum.color
      }
    })
  }, [currencyValue, distributionValue, indicatorValue, series])

  const handleTooltipFormatter = useCallback(
    function () {
      return `
      <ul style="list-style: none; padding-left: 0; margin: 0;">
        ${this.points
          .map(point => {
            if (!point || point.y === 0) return ''

            const formattedValue = formatAmount(point.y)

            const val =
              indicatorValue === INDICATORS_EMT_DICTIONARY.amount
                ? currencyValue
                  ? `USD ${formattedValue}`
                  : `$${formattedValue}`
                : formattedValue

            return `<li><strong>${val}</strong> transados por ${point.series.name} empresa.</li><br/>`
          })
          .join('')}
      </ul>
    `
    },
    [yearValue, distributionValue, indicatorValue, sectorValue, currencyValue]
  )

  let headerDef
  let recordsDecoratorsDef
  if (indicatorValue === INDICATORS_EMT_DICTIONARY.amount) {
    headerDef = [
      { label: 'Mes del Año', value: 'month' },
      { label: 'Micro', value: currencyValue ? 'microUSD' : 'microCLP' },
      { label: 'Pequeña', value: currencyValue ? 'smallUSD' : 'smallCLP' },
      { label: 'Mediana', value: currencyValue ? 'mediumUSD' : 'mediumCLP' },
      { label: 'Total EMT', value: currencyValue ? 'totalEMT_USD' : 'totalEMT_CLP' }
    ]
    recordsDecoratorsDef = [
      d => d.month,
      d => (currencyValue ? `USD ${formatAmount(d.microUSD)}` : `$${formatAmount(d.microCLP)}`),
      d => (currencyValue ? `USD ${formatAmount(d.smallUSD)}` : `$${formatAmount(d.smallCLP)}`),
      d => (currencyValue ? `USD ${formatAmount(d.mediumUSD)}` : `$${formatAmount(d.mediumCLP)}`),
      d => (currencyValue ? `USD ${formatAmount(d.totalEMT_USD)}` : `$${formatAmount(d.totalEMT_CLP)}`)
    ]
  } else if (indicatorValue === INDICATORS_EMT_DICTIONARY.orders) {
    headerDef = [
      { label: 'Mes del Año', value: 'month' },
      { label: 'Micro', value: 'microOrders' },
      { label: 'Pequeña', value: 'smallOrders' },
      { label: 'Mediana', value: 'mediumOrders' },
      { label: 'Total EMT', value: 'totalOrders' }
    ]
    recordsDecoratorsDef = [
      d => d.month,
      d => formatAmount(d.microOrders),
      d => formatAmount(d.smallOrders),
      d => formatAmount(d.mediumOrders),
      d => formatAmount(d.totalOrders)
    ]
  } else {
    headerDef = [
      { label: 'Mes del Año', value: 'month' },
      { label: 'Micro', value: 'quantityOffersMicro' },
      { label: 'Pequeña', value: 'quantityOffersSmall' },
      { label: 'Mediana', value: 'quantityOffersMedium' },
      { label: 'Total EMT', value: 'totalOffers' }
    ]
    recordsDecoratorsDef = [
      d => d.month,
      d => formatAmount(d.quantityOffersMicro),
      d => formatAmount(d.quantityOffersSmall),
      d => formatAmount(d.quantityOffersMedium),
      d => formatAmount(d.totalOffers)
    ]
  }

  useEffect(() => {
    if (!yearValue) return
    dispatch(onGetSmallerCompaniesBySectorThunk({ year: yearValue, sector: sectorValue }))
  }, [endYear, sectorValue])
  useEffect(() => {
    if (!endYear) return
    dispatch(onGetSmallerCompaniesBySectorThunk({ year: endYear, sector: sectorValue }))
  }, [endYear])
  return (
    <Root>
      <Content>
        <Typography
          color="primary"
          fontSize="18px"
          fontWeight="bold"
          lineHeight="21px"
          margin="0 0 24px"
          variant="body1"
        >
          Monto y Órdenes de compra por sector
        </Typography>
        <GridContainer>
          <GridItem xs={5}>
            <Title>Cifras mensuales de Empresas de menor tamaño</Title>
          </GridItem>
        </GridContainer>
        <GridContainer justifyContent="space-between" spacing={2}>
          <GridItem xs={6}>
            <Description>
              Conoce la información de montos y la cantidad de Órdenes de compra realizadas a Empresas de menor tamaño.
              Puedes filtrar la información según sector del Estado y el año. Además de seleccionar la información que
              requieres visualizar
            </Description>
          </GridItem>
          <GridItem xs={5}>
            <CardCustom color="primary">
              <GridItem xs={1} padding="10px 0 0 0">
                <Info>
                  <InfoCircle>
                    <ExclamationIcon />
                  </InfoCircle>
                </Info>
              </GridItem>
              <GridItem xs={11} padding="10px 4px">
                <GridItem xs={12}>
                  <Typography fontSize="14px" lineHeight="16px" variant="body1">
                    El tamaño del proveedor es información provista por el SII. <br />
                    <DialogLink onClick={handleOpenHelp}>Ver información completa</DialogLink>
                  </Typography>
                </GridItem>
              </GridItem>
            </CardCustom>
          </GridItem>
        </GridContainer>

        <DataLayout
          disableCaptureImage={disableCaptureImage}
          disableDownloadFile={disableDownloadFile}
          header={
            <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
              <GridItem md={10} xs={12}>
                <GridContainer alignItems="center" spacing={1}>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Año"
                      loading={loading}
                      options={yearsOptions}
                      value={yearValue}
                      onChange={handleChangeYear}
                    />
                  </GridItem>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Sector del estado"
                      loading={loading}
                      options={sectorOptions}
                      value={sectorValue}
                      onChange={handleChangeSector}
                    />
                  </GridItem>
                  <GridItem md={3} xs={12}>
                    <Select
                      label="Indicador"
                      loading={loading}
                      options={INDICATORS_EMT_OPTIONS}
                      value={indicatorValue}
                      onChange={handleChangeIndicador}
                    />
                  </GridItem>

                  <GridItem md={3} xs={12}>
                    <Select
                      label="Distribución"
                      loading={loading}
                      options={DISTRIBUTION_EMT_OPTIONS}
                      value={distributionValue}
                      onChange={handleChangeDistribution}
                    />
                  </GridItem>
                </GridContainer>
              </GridItem>
              <GridItem md={2} xs={12} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Switch
                  checked={currencyValue}
                  color="primary"
                  labelLeft="CLP"
                  labelRight="USD"
                  onChange={handleChangeCurrency}
                  margin="0 0 0 32px"
                  disabled={INDICATORS_EMT_DICTIONARY.amount !== indicatorValue}
                />
              </GridItem>
            </GridContainer>
          }
          infographic={<InfoSmallerCompaniesBySector />}
          noData={!loading && data?.length === 0}
          noDataText="El organismo comprador no cuenta con información relacionada a sus proveedores durante el año seleccionado."
          ref={dataLayoutRef}
          updatedDate={latestUpdate}
          onCaptureImage={handleCaptureImage}
          onChangeView={handleChangeView}
          onDownloadFile={handleDownloadFile}
          paddingFooter="10px"
        >
          <DataLayoutChild view={DataLayoutViews.CHART}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 1px">
                <GridItem xs={12}>
                  <StackedBarChart
                    key={`chart-${currencyValue}-${distributionValue}-${indicatorValue}`}
                    categories={months}
                    data={enhancedData}
                    id="smaller-companies-by-sector-chart"
                    tooltipFormatter={handleTooltipFormatter}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
          <DataLayoutChild view={DataLayoutViews.TABLE}>
            <Card color="blank" elevation="1" margin="0 0 32px">
              <GridContainer padding="0 0 20px">
                <GridItem xs={12}>
                  <DataTable
                    headers={headerDef}
                    headersCellsProps={[{}, {}, {}, {}, {}]}
                    loading={loading}
                    initialRows={12}
                    records={distributionValue === DISTRIBUTION_DICTIONARY.Monthly ? data : dataAccumulated}
                    recordsDecorators={recordsDecoratorsDef}
                  />
                </GridItem>
              </GridContainer>
            </Card>
          </DataLayoutChild>
        </DataLayout>
        <InfoEMTDialog open={helpOpen} onClose={handleCloseHelp} />
      </Content>
    </Root>
  )
}

export default SmallerCompaniesBySector
